//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import Radium from "radium";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// work-preference-goals imports
import IGoalsWrapper from "./goals-wrapper.d";
import { BaseGoalComponent } from "./base-goal.component";
import * as styles from "./goals-wrapper.styles";
// other internals
import ReduxState from "../../declarations/state.d";
import { upsertPreferredWork } from "@mutations";
import { standardGoals } from "@util/goal.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { H2 } from "@ui";
import { track, TrackingEvents } from "@util/analytics.util";
// material-ui
import { FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
@Radium
export class WorkPreferenceGoalComponent extends BaseGoalComponent<IGoalsWrapper.IWorkPreference.State> {
  constructor(props: IGoalsWrapper.Props) {
    super(props, standardGoals.PreferredWork);

    this.state = this.calculateStateFromProps();
    this.onWorkPreferenceSelected = this.onWorkPreferenceSelected.bind(this);
    this.onWorkPreferenceMutationFailure = this.onWorkPreferenceMutationFailure.bind(this);
    this.onMutationFailure = this.onMutationFailure.bind(this);
  }

  private calculateStateFromProps(): IGoalsWrapper.IWorkPreference.State {
    const goal = this.findGoal();
    const goalOptions = goal && goal.options;

    const preferredWorkOptions =
      goalOptions && (goalOptions.specificOptions as ISchema.IGoalPreferredWorkOptions);

    return {
      selected: !!goal,
      loading: false,
      workPreference: ((preferredWorkOptions && preferredWorkOptions.preferredWorkType) ||
        "Any") as ISchema.PreferredWorkType,
      keepEndOfDayOpen: (preferredWorkOptions && preferredWorkOptions.keepEndOfDayOpen) || false,
      keepStartOfDayOpen:
        (preferredWorkOptions && preferredWorkOptions.keepStartOfDayOpen) || false,
    };
  }

  protected mutateOnToggle(): void {
    // no toggle for this component
  }

  private onWorkPreferenceMutationSuccess(workPreference: ISchema.PreferredWorkType) {
    // set the state!
    this.setState({ workPreference });
    track(TrackingEvents.SETTINGS.UPDATE_PREFERRED_WORK, { workPreference });

    // message
    let message: string;
    switch (workPreference) {
      case "Morning":
        message = `Great! We'll try to keep your morning open`;
        break;
      case "Afternoon":
        message = `Great! We'll try to keep your afternoon open`;
        break;
      case "Any":
      default:
        message = `Okay, we'll slot meetings according to your other preferences`;
        break;
    }

    this.props.snackBarOpen(message);
  }

  private onWorkPreferenceMutationFailure(): void {
    const message = "An error occurred while updating work preference";
    this.props.snackBarOpen(message);
  }

  private onWorkPreferenceSelected(
    _event: React.ChangeEvent<{}>,
    workPreference: ISchema.PreferredWorkType,
  ): void {
    const org = this.props.org;
    const options = { preferredWorkType: workPreference };

    upsertPreferredWork(
      this.props.relay.environment,
      { options, orgId: org.id },
      this.onWorkPreferenceMutationSuccess.bind(this, workPreference),
      this.onWorkPreferenceMutationFailure,
    );
  }

  private onMutationFailure(): void {
    this.props.snackBarOpen("An error occurred!");
  }

  private renderPreferredWork(): JSX.Element {
    const { classes } = this.props;

    return (
      <RadioGroup
        name="preferredWork"
        value={this.state.workPreference}
        onChange={this.onWorkPreferenceSelected}
        style={styles.radioContainer}
      >
        <FormControlLabel
          control={<Radio color="primary" />}
          value="Morning"
          label="Morning"
          disabled={this.state.loading}
          classes={{ label: classes.radioText }}
        />
        <FormControlLabel
          control={<Radio color="primary" />}
          value="Afternoon"
          label="Afternoon"
          disabled={this.state.loading}
          classes={{ label: classes.radioText }}
        />
        <FormControlLabel
          control={<Radio color="primary" />}
          value="Any"
          label="No Preference"
          disabled={this.state.loading}
          classes={{ label: classes.radioText }}
        />
      </RadioGroup>
    );
  }

  public render(): JSX.Element {
    return (
      <div style={styles.goal}>
        {!this.props.isWebSettings ? (
          <H2>When do you prefer to have uninterrupted time for work?</H2>
        ) : (
          ""
        )}
        {this.renderPreferredWork()}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const WorkPreferenceGoalStyled = withStyles(styles.classes)(WorkPreferenceGoalComponent);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: ReduxState.Redux, _ownProps: IGoalsWrapper.Container) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(WorkPreferenceGoalStyled);

//////////////////
// RELAY
//////////////////
import { fragments } from "./WorkPreferenceGoal.gql";
export const WorkPreferenceGoal = createFragmentContainer(reduxContainer, fragments);
